import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { formatNumber } from '../../utils/format';

export interface LiveStats {
  calls?: number;
  emails?: number;
  leads?: number;
  conversations?: number;
  followUps?: number;
  webInteractions?: number;
}

export interface LiveDateRange {
  from: Date | null;
  to: Date | null;
}

function formatDateRangeLabel(dateRange?: LiveDateRange): string {
  if (!dateRange?.from) return '';
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const now = new Date();
  const from = dateRange.from;
  const to = dateRange.to ?? now;

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(from, to)) return `on ${fmt(from)}`;
  if (sameDay(to, now)) return `since ${fmt(from)}`;
  return `${fmt(from)} – ${fmt(to)}`;
}

export function buildLiveMessages(
  stats: LiveStats | undefined,
  dateRange?: LiveDateRange
): string[] {
  if (!stats) return ['Working your leads'];
  const suffix = formatDateRangeLabel(dateRange);
  const s = suffix ? ` · ${suffix}` : '';
  const items: string[] = [];
  if (stats.calls) items.push(`Called ${formatNumber(stats.calls)} of your leads${s}`);
  if (stats.emails) items.push(`Sent ${formatNumber(stats.emails)} property alerts${s}`);
  if (stats.leads) items.push(`Captured ${formatNumber(stats.leads)} new leads${s}`);
  if (stats.conversations) items.push(`Had ${formatNumber(stats.conversations)} conversations${s}`);
  if (stats.followUps)
    items.push(`${formatNumber(stats.followUps)} upcoming follow-ups queued for you`);
  if (stats.webInteractions)
    items.push(`Tracked ${formatNumber(stats.webInteractions)} site visits${s}`);
  return items.length > 0 ? items : ['Working your leads'];
}

const ROTATE_INTERVAL = 4000;
const FADE_DURATION = 300;

interface RotatingState {
  index: number;
  fading: boolean;
}

// Singleton-ish store per hook instance via ref
function createRotatingStore(messageCount: number) {
  let state: RotatingState = { index: 0, fading: false };
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setInterval> | null = null;
  let fadeTimer: ReturnType<typeof setTimeout> | null = null;

  function emit() {
    listeners.forEach((l) => l());
  }

  function start() {
    stop();
    if (messageCount <= 1) return;
    timer = setInterval(() => {
      state = { ...state, fading: true };
      emit();
      fadeTimer = setTimeout(() => {
        fadeTimer = null;
        state = { index: (state.index + 1) % messageCount, fading: false };
        emit();
      }, FADE_DURATION);
    }, ROTATE_INTERVAL);
  }

  function stop() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    if (fadeTimer) {
      clearTimeout(fadeTimer);
      fadeTimer = null;
    }
  }

  return {
    getSnapshot: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
    reset: (newCount: number) => {
      messageCount = newCount;
      state = { index: 0, fading: false };
      stop();
      if (listeners.size > 0) start();
    },
  };
}

export function useRotatingMessage(
  stats: LiveStats | undefined,
  dateRange?: LiveDateRange
): {
  message: string;
  fading: boolean;
} {
  const messages = useMemo(() => buildLiveMessages(stats, dateRange), [stats, dateRange]);
  const storeRef = useRef<ReturnType<typeof createRotatingStore> | null>(null);
  const prevCountRef = useRef(messages.length);

  if (!storeRef.current) {
    storeRef.current = createRotatingStore(messages.length);
  }

  if (prevCountRef.current !== messages.length) {
    prevCountRef.current = messages.length;
    storeRef.current.reset(messages.length);
  }

  const subscribe = useCallback(
    (listener: () => void) => storeRef.current!.subscribe(listener),
    []
  );
  const getSnapshot = useCallback(() => storeRef.current!.getSnapshot(), []);

  const { index, fading } = useSyncExternalStore(subscribe, getSnapshot);

  return { message: messages[index % messages.length], fading };
}
